import type { ITileManagerModel, IConnectionModel } from '@/interfaces/models';
import { Side } from '@/schemas/tiles';
import type { TileInstance } from '@/models/tiles/TileInstance';
import { Point } from '@/models/Point';
import { opposite } from '@/utils/direction';
import { getGlobalDirection, getLocalSide, getNeighborPosition } from './utils';

/** Display order only: preserve graph lane identity while letting cables nest around bends. */
export function assignBundleVisualOrder(
  manager: ITileManagerModel,
  connections?: IConnectionModel,
) {
  const links = new Map<string, Array<[string, boolean]>>();
  const endpoints = new Map<string, [TileInstance, Side]>();
  const key = (tile: TileInstance, side: Side) => `${tile.id}:${side}`;
  const enabled = (tile: TileInstance, side: Side) =>
    connections
      ? connections.connectionFlags(tile.id)[side]
      : (tile.meta.connections?.[side] ?? true);
  const link = (a: string, b: string, reverse: boolean) => {
    links.get(a)?.push([b, reverse]);
    links.get(b)?.push([a, reverse]);
  };
  const tiles = manager.tiles
    .filter((tile) => tile.meta.bundle || tile.tileType === 'BundleInterface')
    .sort((a, b) => a.location.y - b.location.y || a.location.x - b.location.x);
  for (const tile of tiles) {
    tile.laneVisualFlips = {};
    for (const side of Object.values(Side)) {
      if (
        (tile.tileType === 'BundleInterface' && side !== Side.LEFT) ||
        !enabled(tile, side) ||
        tile.meta.bundle?.sides?.[side]?.length === 0
      )
        continue;
      endpoints.set(key(tile, side), [tile, side]);
      links.set(key(tile, side), []);
    }
  }
  for (const tile of tiles) {
    const full = tile.meta.bundle
      ? Object.values(Side).filter(
          (side) =>
            endpoints.has(key(tile, side)) &&
            (tile.meta.bundle?.sides?.[side]?.length ?? tile.meta.bundle!.width) ===
              tile.meta.bundle!.width,
        )
      : [];
    // Choose the physical trunk. A multiway junction keeps its two opposite sides continuous.
    const pair =
      full.includes(Side.LEFT) && full.includes(Side.RIGHT)
        ? [Side.LEFT, Side.RIGHT]
        : full.includes(Side.TOP) && full.includes(Side.BOTTOM)
          ? [Side.TOP, Side.BOTTOM]
          : full.slice(0, 2);
    if (pair.length === 2) {
      const worldSide = pair.map((side) =>
        getLocalSide(getGlobalDirection(side, tile.meta.rotation)!, 0),
      );
      const reverse =
        (worldSide.includes(Side.TOP) && worldSide.includes(Side.RIGHT)) ||
        (worldSide.includes(Side.BOTTOM) && worldSide.includes(Side.LEFT));
      link(key(tile, pair[0]), key(tile, pair[1]), reverse);
    }
    for (const side of Object.values(Side)) {
      if (!endpoints.has(key(tile, side))) continue;
      const direction = getGlobalDirection(side, tile.meta.rotation)!;
      const neighbor = manager.get(
        getNeighborPosition(Point.fromData(tile.location), direction),
      );
      if (!neighbor || (!neighbor.meta.bundle && neighbor.tileType !== 'BundleInterface'))
        continue;
      const neighborSide = getLocalSide(opposite(direction), neighbor.meta.rotation)!;
      if (!endpoints.has(key(neighbor, neighborSide))) continue;
      const count = tile.laneNodeIds?.[side]?.length;
      if (count && count === neighbor.laneNodeIds?.[neighborSide]?.length)
        link(key(tile, side), key(neighbor, neighborSide), false);
    }
  }
  const assigned = new Map<string, boolean>();
  for (const root of endpoints.keys()) {
    if (assigned.has(root)) continue;
    assigned.set(root, false);
    const queue = [root];
    for (let i = 0; i < queue.length; i++) {
      const current = queue[i];
      const flip = assigned.get(current)!;
      const [tile, side] = endpoints.get(current)!;
      tile.laneVisualFlips![side] = flip;
      for (const [next, reverse] of links.get(current) ?? []) {
        if (assigned.has(next)) continue;
        assigned.set(next, flip !== reverse);
        queue.push(next);
      }
    }
  }
}
